import * as yup from 'yup';

const requiredFieldMessage = (fieldName: string): string => `${fieldName} is a required field.`;

export const paymentSchema = yup.object().shape({
  considerationEnabled: yup.boolean().required(requiredFieldMessage('Consideration')),
  totalConsideration: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' || originalValue === null ? undefined : value))
    .when('considerationEnabled', {
      is: true,
      then: (schema) =>
        schema
          .min(0.01, 'Total consideration must be greater than zero')
          .required(requiredFieldMessage('Total consideration')),
      otherwise: (schema) => schema.notRequired(),
    }),
  hasHardMoneyLoan: yup.boolean().when('considerationEnabled', {
    is: true,
    then: (schema) => schema.required(requiredFieldMessage('Hard money loan')),
    otherwise: (schema) => schema.notRequired(),
  }),
  hardMoneyLoanType: yup.string().when(['considerationEnabled', 'hasHardMoneyLoan'], {
    is: (considerationEnabled: boolean, hasHardMoneyLoan: boolean) =>
      considerationEnabled === true && hasHardMoneyLoan === true,
    then: (schema) => schema.required(requiredFieldMessage('Hard money loan type')),
    otherwise: (schema) => schema.notRequired(),
  }),
  hardMoneyLoanOtherDescription: yup
    .string()
    .trim()
    .when(['considerationEnabled', 'hardMoneyLoanType'], {
      is: (considerationEnabled: boolean, hardMoneyLoanType: string) =>
        considerationEnabled === true && hardMoneyLoanType === 'OTHER',
      then: (schema) => schema.required(requiredFieldMessage('Other loan description')),
      otherwise: (schema) => schema.notRequired(),
    }),
  payments: yup.mixed().when('considerationEnabled', {
    is: true,
    then: () =>
      yup
        .array()
        .of(
          yup.object().shape({
            id: yup.string().required(requiredFieldMessage('Payment ID')),
            amount: yup
              .number()
              .transform((value, originalValue) => (originalValue === '' || originalValue === null ? undefined : value))
              .min(0.01, 'Payment amount must be greater than zero')
              .required(requiredFieldMessage('Amount')),
            paymentMethod: yup.string().required(requiredFieldMessage('Payment method')),
            otherPaymentMethodDescription: yup
              .string()
              .trim()
              .when('paymentMethod', {
                is: 'OTHER',
                then: (schema) => schema.required(requiredFieldMessage('Other payment method description')),
                otherwise: (schema) => schema.notRequired(),
              }),
            currencyCode: yup.string().when('paymentMethod', {
              is: 'FOREIGN_CURRENCY',
              then: (schema) => schema.required(requiredFieldMessage('Currency code')),
              otherwise: (schema) => schema.notRequired(),
            }),
            currencyNotListedDescription: yup
              .string()
              .trim()
              .when(['paymentMethod', 'currencyCode'], {
                is: (paymentMethod: string, currencyCode: string) =>
                  paymentMethod === 'FOREIGN_CURRENCY' && currencyCode === 'NOT_LISTED',
                then: (schema) => schema.required(requiredFieldMessage('Currency description')),
                otherwise: (schema) => schema.notRequired(),
              }),
            fromFinancialInstitution: yup.boolean().required(requiredFieldMessage('From financial institution')),
            accountNumber: yup
              .string()
              .trim()
              .matches(/^[a-zA-Z0-9]*$/, 'Account number must contain only alphanumeric characters')
              .when('fromFinancialInstitution', {
                is: true,
                then: (schema) => schema.required(requiredFieldMessage('Account number')),
                otherwise: (schema) => schema.notRequired(),
              }),
            financialInstitutionName: yup
              .string()
              .trim()
              .when('fromFinancialInstitution', {
                is: true,
                then: (schema) => schema.required(requiredFieldMessage('Financial institution legal name')),
                otherwise: (schema) => schema.notRequired(),
              }),
            associatedWithAllTransferees: yup
              .boolean()
              .required(requiredFieldMessage('Associated with all transferees')),
            associatedTransfereeNames: yup
              .array()
              .of(
                yup
                  .string()
                  .trim()
                  .required(requiredFieldMessage('Transferee name'))
                  .test(
                    'not-empty',
                    requiredFieldMessage('Transferee name'),
                    (value) => value != null && value.trim().length > 0,
                  ),
              )
              .when('associatedWithAllTransferees', {
                is: false,
                then: (schema) =>
                  schema
                    .min(1, requiredFieldMessage('At least one associated transferee name'))
                    .required(requiredFieldMessage('Associated transferee names')),
                otherwise: (schema) => schema.notRequired(),
              }),
            allPayorsAreTransferees: yup.boolean().required(requiredFieldMessage('All payors are transferees')),
            payors: yup
              .array()
              .of(
                yup.object().shape({
                  id: yup.string().required(requiredFieldMessage('Payor ID')),
                  type: yup.string().required(requiredFieldMessage('Payor type')),
                  legalName: yup
                    .string()
                    .trim()
                    .max(100, 'Maximum of 100 characters')
                    .when('type', {
                      is: 'ENTITY',
                      then: (schema) => schema.required(requiredFieldMessage('Payor legal name')),
                      otherwise: (schema) => schema.notRequired(),
                    }),
                  firstName: yup
                    .string()
                    .trim()
                    .max(50, 'Maximum of 50 characters')
                    .when('type', {
                      is: 'INDIVIDUAL',
                      then: (schema) => schema.required(requiredFieldMessage('First name')),
                      otherwise: (schema) => schema.notRequired(),
                    }),
                  middleName: yup
                    .string()
                    .trim()
                    .test('max-length', 'Maximum of 50 characters', (value) => !value || value.length <= 50)
                    .notRequired(),
                  lastName: yup
                    .string()
                    .trim()
                    .max(50, 'Maximum of 50 characters')
                    .when('type', {
                      is: 'INDIVIDUAL',
                      then: (schema) => schema.required(requiredFieldMessage('Last name')),
                      otherwise: (schema) => schema.notRequired(),
                    }),
                  suffix: yup
                    .string()
                    .trim()
                    .test('max-length', 'Maximum of 20 characters', (value) => !value || value.length <= 20)
                    .notRequired(),
                }),
              )
              .when('allPayorsAreTransferees', {
                is: false,
                then: (schema) =>
                  schema.min(1, requiredFieldMessage('At least one payor')).required(requiredFieldMessage('Payors')),
                otherwise: (schema) => schema.notRequired(),
              }),
          }),
        )
        .required(requiredFieldMessage('Payments')),
    otherwise: () => yup.mixed().notRequired(),
  }),
});
